import { sorter, sortTime, sortRank } from '@/pages/intelligence/utils/sort';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import type { TableSelectionType } from '@intelligence/components/TableComponents/components/TableCheckbox';

export const PAGE_SIZES_SMALL = [5, 10, 15];
export const PAGE_SIZES_LARGET = [20, 50, 100];

interface SortParamsType {
  ratioType: 0 | 1;
  sortVal: 'desc' | 'asc';
  sortMetric: string;
  sortSource?: string;
}

type SortTableOptions = {
  tableData: any[];
  sortParams: SortParamsType;
  isRank?: boolean;
  valueProperty?: 'value' | 'value_str' | 'this';
  timeProperty?: 'date' | 'time' | 'release_time';
  metricProperty?: 'metric' | 'name' | 'title';
  growthProperty?: 'dod' | 'growth_ratio' | 'ratio';
  sourceProperty?: 'metric' | 'source';
  listProperty?: 'metric_list' | 'metrics' | 'metric_value_list';
  getMetricValueFun?: Function;
};

/**
 * 指标单元格排序用：是否为「缺数」（应始终排在有效数字之后）。
 * 注意：不能用 `!val` —— 数值/环比为 0 是有效值，应排在负数与正数之间。
 */
export const isMetricSortValueMissing = (val: any): boolean => {
  if (val === null || val === undefined) {
    return true;
  }
  if (typeof val === 'number') {
    return Number.isNaN(val);
  }
  if (typeof val === 'boolean') {
    return true;
  }
  const raw = typeof val === 'string' ? val.trim() : String(val);
  if (raw === '' || raw === '-' || raw === '—') {
    return true;
  }
  const n = parseFloat(raw.replace(/,/g, ''));
  return Number.isNaN(n);
};
/** 获取对应的指标名数据条目 */
const getMetricValue = (params: {
  cvm: any;
  sortParams: SortParamsType;
  metricProperty?: 'metric' | 'name' | 'title';
  listProperty?: 'metric_list' | 'metrics';
  sourceProperty?: 'metric' | 'source';
}) => {
  const {
    cvm,
    sortParams,
    metricProperty = 'metric',
    listProperty = 'metrics',
    sourceProperty,
  } = params;
  const metrics = Array.isArray(cvm?.[listProperty]) ? cvm?.[listProperty] : [];
  if (sourceProperty && metricProperty) {
    const metric = metrics.find(
      (item: any) =>
        item?.[metricProperty] === sortParams?.sortMetric &&
        item?.[sourceProperty] === sortParams?.sortSource,
    );
    return metric;
  }
  const metric = metrics.find((item: any) => item?.[metricProperty] === sortParams?.sortMetric);
  return metric;
};

/** 排序表格：根据指定的指标和环比参数对表数据进行排序 */
export const sortTableByMetric = ({
  tableData, // 要排序的数据表
  sortParams, // 包含排序参数的对象
  isRank,
  timeProperty = 'date',
  valueProperty = 'value',
  metricProperty = 'metric', // 要排序的指标属性，默认为 'metric'
  growthProperty = 'growth_ratio', // 排序的环比属性，默认为 'growth_ratio'
  listProperty = 'metrics',
  sourceProperty,
  getMetricValueFun,
}: SortTableOptions): any[] => {
  if (!Array.isArray(tableData)) {
    return [];
  }
  // 创建一个新数组，用于存放排序后的数据
  const sortTable = tableData.slice(0);
  if (sortParams?.sortMetric === timeProperty) {
    sortTable.sort((first, second) => {
      const firstVal = first?.[timeProperty];
      const secondVal = second?.[timeProperty];
      return sortParams?.sortVal === 'asc'
        ? sortTime(secondVal, firstVal)
        : sortTime(firstVal, secondVal);
    });
  } else {
    sortTable.sort((first, second) => {
      // 如果比较的两行数据中有一个是 'total'，则不进行排序
      if (first?.app_id === 'total' || second?.app_id === 'total') {
        return 0;
      }
      // 获取指标值
      const firstMetric = getMetricValueFun
        ? getMetricValueFun(first)
        : getMetricValue({
            cvm: first,
            sortParams,
            metricProperty,
            listProperty,
            sourceProperty,
          });
      // : getMetricValue(first, sortParams, metricProperty, listProperty);
      const secondMetric = getMetricValueFun
        ? getMetricValueFun(second)
        : getMetricValue({
            cvm: second,
            sortParams,
            metricProperty,
            listProperty,
            sourceProperty,
          });
      // : getMetricValue(second, sortParams, metricProperty, listProperty);
      // 根据指定的类型（例如：ratioType）获取度量值或增长属性值
      const firstVal =
        sortParams?.ratioType === 1 ? firstMetric?.[growthProperty] : firstMetric?.[valueProperty];
      const secondVal =
        sortParams?.ratioType === 1
          ? secondMetric?.[growthProperty]
          : secondMetric?.[valueProperty];

      // 缺数沉底（与升/降序无关）；0 为有效数值，参与正常负→0→正顺序
      if (isMetricSortValueMissing(firstVal) && isMetricSortValueMissing(secondVal)) {
        return 0;
      }
      if (isMetricSortValueMissing(firstVal)) {
        return 1;
      }
      if (isMetricSortValueMissing(secondVal)) {
        return -1;
      }

      const sorterFun = isRank ? sortRank : sorter;
      return sortParams?.sortVal === 'asc'
        ? sorterFun(firstVal, secondVal)
        : sorterFun(secondVal, firstVal);
    });
  }
  return sortTable;
};

/** 查找对应的metric条目 */
export const getMetrics = (list: any[], metric: string, metricName: string) => {
  if (Array.isArray(list) && list.length > 0) {
    // 查找具有匹配 metric 的对象
    return list.find((item) => item[metricName] === metric);
  }
};

export const getItemFromArray = (param: {
  list: any[];
  key: string;
  keyVal: string;
}): any | null => {
  const { list, key, keyVal } = param;
  if (!Array.isArray(list)) {
    return null;
  }
  // 获取 list 中某个 key 值等于 keyVal 的对象
  const item = list?.find((item) => item[key] === keyVal);
  return item ? item : null;
};

export const handleTableDefaultSelectGames = (
  list: any[],
  idName: string,
): TableSelectionType[] => {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map((item, index) => ({
    ...(item || {}),
    gameId: item?.[idName],
    color: ECHARTSCOLOR[index],
  }));
};
interface IGameRank {
  // 其他属性
  rank?: number;
}
// 这可能会用到
const isRankValid = (rank?: number): boolean => Number(rank) > 0;
// 统一处理排名为"-"的情况
const processRank = (rank?: number): string | number => (rank && isRankValid(rank) ? rank : '-');

// 返回一个对象，包含处理后的排名和是否被禁用的信息
export const getGameRankDetails = (rank?: number) => {
  const rankText = processRank(rank);
  const isDisabled = !isRankValid(rank);

  return { rankText, isDisabled };
};

// 过滤没有排名的游戏
export const filterGamesWithRank = (gamesList: IGameRank[]): any[] => {
  if (!Array.isArray(gamesList)) {
    return [];
  }
  return gamesList.filter((item) => isRankValid(item?.rank));
};
